import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useCommonT } from '../../i18n'
import { navV2Enabled } from '../../lib/navV2'
import { MENU_GROUPS_V2, MENU_ITEMS } from './navMenuItems'

// Secondary-page menu, mounted ONCE in Header. One DOM, two presentations:
// a bounded full-width sheet under the sticky h-14 header on mobile, the
// AuthButton popover idiom on desktop. Accuracy, Blog, Partners and Privacy
// get their first real nav home here.
// `id` keys the label (common:nav.<id>).
// The item lists live in navMenuItems.ts (the search's page inventory test
// reads them).

export default function NavMenu() {
  const { pathname, search } = useLocation()
  const [open, setOpen] = useState(false)
  const t = useCommonT()
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Same click-outside pattern as AuthButton's dropdown, plus Esc-to-close
  // with focus return (disclosure semantics).
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  // Close on any route change: router items already close via onClick; this
  // covers Back/Forward, programmatic navigation, and query-only navigation
  // (e.g. /decks -> /decks?deck=abc via Back) landing under an open menu.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false)
  }, [pathname, search])

  const itemClass = (active: boolean) =>
    // eslint-disable-next-line i18next/no-literal-string -- CSS classes
    `block px-4 py-2.5 md:px-3 md:py-2 text-sm transition-colors ${
      // eslint-disable-next-line i18next/no-literal-string -- CSS classes
      active ? 'text-white bg-gray-800/60' : 'text-gray-200 hover:bg-gray-800'
    }`

  return (
    <div ref={rootRef} className="md:relative">
      <button
        ref={buttonRef}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls="nav-menu-panel"
        aria-label={t('nav.menu')}
        className="-m-2 flex items-center p-2 text-gray-400 hover:text-gray-200 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-5 h-5">
          <path strokeLinecap="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
        </svg>
      </button>

      {open && (
        <nav
          id="nav-menu-panel"
          aria-label={t('nav.more_pages')}
          // Mobile: a sheet pinned under the header, bounded so it can never
          // run under the z-50 bottom tab bar (3.5rem header + 3.5rem bar +
          // the home-indicator inset) and scrollable when the viewport is
          // short (landscape phones). The max-h bound above is the SOLE
          // mechanism keeping the sheet off the tab bar - do not trim the
          // 7rem term believing z-index covers it. z-[60] cannot outrank
          // MobileNav: Header is `sticky z-50`, which creates a stacking
          // context, so this panel's z-[60] is only compared against other
          // header descendants. MobileNav is a SIBLING `fixed z-50` rendered
          // AFTER Header in App.tsx and wins by document order regardless
          // (same trap DeckChatDock's top-of-file comment documents for its
          // own dock). z-[60] is kept anyway - harmless, and it still orders
          // this panel above other header content. The bound also gives back
          // `--ad-inset-bottom` (src/lib/adInset.ts): MobileNav rides above a
          // Mediavine adhesion ad when one is present below md, so the sheet
          // must stop above both. 0px with no ad. Desktop: the AuthButton
          // popover idiom.
          className="fixed inset-x-0 top-14 z-[60] max-h-[calc(100dvh-7rem-env(safe-area-inset-bottom,0px)-var(--ad-inset-bottom,0px))] overflow-y-auto border-b border-gray-800 bg-gray-950 py-1 md:absolute md:inset-x-auto md:right-0 md:top-full md:mt-2 md:w-56 md:max-h-none md:rounded-lg md:border md:border-gray-700 md:bg-gray-900 md:shadow-lg"
        >
          {navV2Enabled() ? MENU_GROUPS_V2.map(group => (
            <div key={group.id} role="group" aria-labelledby={`nav-menu-${group.id}`} className="py-1">
              <div
                id={`nav-menu-${group.id}`}
                className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500 md:px-3"
              >
                {t(`nav.${group.id}`)}
              </div>
              {group.items.map(item => {
                const label = (
                  <span className="flex items-center gap-3">
                    <item.Icon
                      className={`w-5 h-5 shrink-0 ${group.id === 'group_tools' ? 'text-purple-400' : 'text-gray-400'}`}
                    />
                    {t(`nav.${item.id}`)}
                  </span>
                )
                return item.static ? (
                  <a key={item.path} href={item.path} onClick={() => setOpen(false)} className={itemClass(false)}>
                    {label}
                  </a>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setOpen(false)}
                    className={itemClass(pathname === item.path)}
                  >
                    {label}
                  </Link>
                )
              })}
            </div>
          )) : MENU_ITEMS.map(item =>
            'static' in item && item.static ? (
              <a
                key={item.path}
                href={item.path}
                onClick={() => setOpen(false)}
                className={itemClass(false)}
              >
                {t(`nav.${item.id}`)}
              </a>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                className={itemClass(pathname === item.path)}
              >
                {t(`nav.${item.id}`)}
              </Link>
            ),
          )}
        </nav>
      )}
    </div>
  )
}
